import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useMe } from '@/features/auth/useAuth'
import { fmtDate, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { usersMessages } from '@/i18n/messages/users'
import { useWfT } from '@/features/workflow/shared'
import { EditUserDialog, PAGES } from './EditUserDialog'
import { CreateAccountDialog } from './CreateAccountDialog'
import { RolesTab } from './RolesTab'
import { Select } from '@/components/Select'
import { btnPrimary, btnSecondary, inputCls } from './parts'
import { fullName, loginTypeLabel, roleName, type AccessRole, type AuOptions, type DirectoryUser } from './types'

type SortKey = 'name' | 'access' | 'lastLogin'
type Filter = 'active' | 'access' | 'noAccess' | 'never' | 'off' | 'all'
const FILTERS: Filter[] = ['active', 'access', 'noAccess', 'never', 'off', 'all']

const hasAccess = (u: DirectoryUser) => u.isAdmin || u.effectivePages.length > 0

function sortValue(u: DirectoryUser, k: SortKey): string | number {
  switch (k) {
    case 'name': return fullName(u).toLowerCase()
    case 'access': return u.isAdmin ? 99 : u.effectivePages.length
    case 'lastLogin': return u.lastLogin ?? ''
  }
}

function matches(u: DirectoryUser, f: Filter) {
  switch (f) {
    case 'active': return u.active
    case 'access': return u.active && hasAccess(u)
    case 'noAccess': return u.active && !u.denied && !hasAccess(u)
    case 'never': return u.active && !u.lastLogin
    case 'off': return !u.active
    case 'all': return true
  }
}

/** Every AU account and its access, and the roles that open modules (docs/user-management.md). */
export function UsersPage() {
  const t = useT(usersMessages)
  const tc = useT(commonMessages)
  const me = useMe().data
  const meIsAdmin = !!me?.isAdmin
  const [tab, setTab] = useState<'people' | 'roles'>('people')

  const roles = useQuery<AccessRole[]>({ queryKey: ['user-roles'], queryFn: () => api.get<AccessRole[]>('/api/users/roles') })
  const au = useQuery<AuOptions>({ queryKey: ['au-options'], queryFn: () => api.get<AuOptions>('/api/users/au-options') })

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{tc('navUsers')}</h1>
        <p className="mt-1 max-w-prose text-sm text-neutral-500">{t('intro')}</p>
      </header>
      <div role="tablist" className="flex gap-1 border-b border-neutral-200 dark:border-neutral-800">
        {(['people', 'roles'] as const).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-brand-600 text-brand-700 dark:text-brand-300' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            {k === 'people' ? t('tabPeople') : t('tabRoles')}
          </button>
        ))}
      </div>
      {tab === 'people'
        ? <PeopleTab roles={roles.data ?? []} au={au.data} meIsAdmin={meIsAdmin} meId={me?.userId} />
        : <RolesTab roles={roles.data ?? []} meIsAdmin={meIsAdmin} />}
    </div>
  )
}

function PeopleTab({ roles, au, meIsAdmin, meId }: { roles: AccessRole[]; au: AuOptions | undefined; meIsAdmin: boolean; meId: string | undefined }) {
  const qc = useQueryClient()
  const t = useT(usersMessages)
  const tw = useWfT()
  const tc = useT(commonMessages)
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' })
  const [filter, setFilter] = useState<Filter>('active')
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkRole, setBulkRole] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const pageLabel = (k: string) => t(PAGES.find((p) => p.key === k)?.label ?? 'unknown')

  const { data, isPending } = useQuery<DirectoryUser[]>({ queryKey: ['users'], queryFn: () => api.get<DirectoryUser[]>('/api/users/') })

  // The fresh row, so the dialog follows a switch-off or a password reset without reopening.
  const editing = data?.find((u) => u.userId === editingId) ?? null
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f, (data ?? []).filter((u) => matches(u, f)).length])) as Record<Filter, number>, [data])
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = (data ?? []).filter((u) => matches(u, filter) && (!q ||
      [fullName(u), u.username, u.email, u.company].some((s) => s?.toLowerCase().includes(q))))
    const dir = sort.dir === 'asc' ? 1 : -1
    return list.sort((a, b) => {
      const av = sortValue(a, sort.key); const bv = sortValue(b, sort.key)
      const r = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return r * dir
    })
  }, [data, sort, filter, search])

  const toggle = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
  const selectable = rows.filter((u) => u.active && !u.denied)
  const allSelected = selectable.length > 0 && selectable.every((u) => selected.has(u.userId))

  const bulk = useMutation({
    mutationFn: () => api.post<{ updated: number; skipped: number }>('/api/users/roles/assign', { userIds: [...selected], roles: [bulkRole] }),
    onSuccess: (r) => {
      setNotice(t('bulkDone', { n: r.updated, skipped: r.skipped })); setSelected(new Set())
      void qc.invalidateQueries({ queryKey: ['users'] }); void qc.invalidateQueries({ queryKey: ['user-roles'] })
    },
    onError: (e) => setNotice(e instanceof ApiError ? e.message : t('couldNotSave')),
  })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${inputCls} max-w-xs`} type="search" value={search} placeholder={t('search')} onChange={(e) => setSearch(e.target.value)} />
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} aria-pressed={filter === f}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${filter === f ? 'bg-neutral-900 text-white ring-neutral-900 dark:bg-white dark:text-neutral-900 dark:ring-white' : 'text-neutral-600 ring-neutral-300 hover:bg-neutral-50 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800'}`}>
              {t(`filter_${f}` as const)} <span className="tabular opacity-60">{counts[f]}</span>
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <button onClick={() => setCreating(true)} disabled={!au?.enabled} title={au?.enabled ? undefined : t('auOff')} className={btnPrimary}>{t('newLogin')}</button>
        </div>
      </div>
      {au && !au.enabled && <p className="text-xs text-neutral-500">{t('auOff')}</p>}

      {(selected.size > 0 || notice) && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-brand-50 px-3 py-2 text-sm dark:bg-brand-500/10">
          {selected.size > 0 ? <>
            <span className="font-medium">{t('selected', { n: selected.size })}</span>
            <Select className="min-w-[12rem]" value={bulkRole} onChange={setBulkRole}
              empty={{ value: '', label: `${t('giveRole')}…` }}
              options={roles.map((r) => ({ value: r.role, label: roleName(tw, r.role, roles) }))} />
            <button onClick={() => { setNotice(null); bulk.mutate() }} disabled={!bulkRole || bulk.isPending} className={btnPrimary}>{t('giveRoleApply')}</button>
            <button onClick={() => setSelected(new Set())} className={btnSecondary}>{t('clearSelection')}</button>
          </> : <>
            <span>{notice}</span>
            <button onClick={() => setNotice(null)} className="ml-auto text-xs text-neutral-500 hover:underline">{tc('close')}</button>
          </>}
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
              <th className="w-8 px-3 py-2">
                <input type="checkbox" aria-label={t('selectAll')} checked={allSelected} className="rounded border-neutral-300 text-brand-600"
                       onChange={() => setSelected(allSelected ? new Set() : new Set(selectable.map((u) => u.userId)))} />
              </th>
              <Th col="name" sort={sort} onSort={toggle}>{t('colUser')}</Th>
              <th className="px-3 py-2 font-medium">{t('colAuType')}</th>
              <th className="px-3 py-2 font-medium">{t('colRoles')}</th>
              <Th col="access" sort={sort} onSort={toggle}>{t('colAccess')}</Th>
              <Th col="lastLogin" sort={sort} onSort={toggle}>{t('colLastLogin')}</Th>
              <th className="px-3 py-2 text-right font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.userId} className={`border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40 ${u.active ? '' : 'opacity-60'}`}>
                <td className="px-3 py-2">
                  {u.active && !u.denied && <input type="checkbox" aria-label={fullName(u)} checked={selected.has(u.userId)} className="rounded border-neutral-300 text-brand-600"
                    onChange={() => setSelected((prev) => { const n = new Set(prev); if (n.has(u.userId)) n.delete(u.userId); else n.add(u.userId); return n })} />}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">
                    {fullName(u) || t('unknown')}
                    {!u.active && <span className="ml-2 whitespace-nowrap rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200">{t('switchedOff')}</span>}
                  </div>
                  <div className="text-xs text-neutral-400">{u.username}{u.email ? ` · ${u.email}` : ''}{u.company ? ` · ${u.company}` : ''}</div>
                </td>
                <td className="px-3 py-2 text-xs text-neutral-600 dark:text-neutral-300">{loginTypeLabel(u.loginType)}</td>
                <td className="px-3 py-2">
                  {u.roles.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => <span key={r} className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-800 dark:bg-brand-500/15 dark:text-brand-200">{roleName(tw, r, roles)}</span>)}
                    </div>
                  ) : u.suggestedRoles.length > 0 && u.active ? (
                    <span className="text-[11px] text-sky-700 dark:text-sky-300">{t('suggestedRole', { roles: u.suggestedRoles.map((r) => roleName(tw, r, roles)).join(', ') })}</span>
                  ) : <span className="text-xs text-neutral-300 dark:text-neutral-600">—</span>}
                </td>
                <td className="px-3 py-2">
                  {u.denied ? <span className="text-xs text-neutral-400">{t('denied')}</span>
                    : u.isAdmin ? <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">{t('admin')}</span>
                    : u.effectivePages.length === 0 ? <span className="text-xs text-neutral-400">{t('noAccess')}</span>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {u.effectivePages.map((p) => <span key={p} className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{pageLabel(p)}</span>)}
                      </div>
                    )}
                </td>
                <td className="px-3 py-2 text-neutral-500">{u.lastLogin ? fmtDate(u.lastLogin) : <span className="text-xs text-neutral-400">{t('neverSignedIn')}</span>}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditingId(u.userId)}
                          className="rounded-md px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/30 hover:bg-brand-50 dark:text-brand-100 dark:ring-brand-100/20 dark:hover:bg-brand-700/20">
                    {tc('edit')}
                  </button>
                </td>
              </tr>
            ))}
            {!isPending && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-neutral-500">{t('empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && <EditUserDialog key={editing.userId} user={editing} roles={roles} au={au} meIsAdmin={meIsAdmin} meId={meId} onClose={() => setEditingId(null)} />}
      {creating && au && <CreateAccountDialog au={au} roles={roles} onClose={() => setCreating(false)} />}
    </div>
  )
}

function Th({ col, sort, onSort, children }: {
  col: SortKey
  sort: { key: SortKey; dir: 'asc' | 'desc' }
  onSort: (k: SortKey) => void
  children: React.ReactNode
}) {
  const active = sort.key === col
  return (
    <th className="px-3 py-2 font-medium">
      <button type="button" onClick={() => onSort(col)}
              className={`inline-flex items-center gap-1 hover:text-neutral-800 dark:hover:text-neutral-200 ${active ? 'text-neutral-800 dark:text-neutral-200' : ''}`}>
        {children}
        <span aria-hidden className="text-[10px]">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  )
}
